import React from 'react';
import { motion } from 'framer-motion';

/**
 * SlideInsights Component
 * Displays AI-generated insights (strengths, risks, opportunities)
 * 
 * Features:
 * - Icon-based categorization
 * - Color-coded insights
 * - Animated entry
 * - Expandable details (optional)
 */

const InsightItem = ({ text, icon, type, delay = 0 }) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'strength':
        return {
          bg: 'bg-green-500/10 border-green-500/30',
          text: 'text-green-700 dark:text-green-400',
          iconBg: 'bg-green-500/20',
          icon: icon || '💡'
        };
      case 'risk':
        return {
          bg: 'bg-red-500/10 border-red-500/30',
          text: 'text-red-700 dark:text-red-400',
          iconBg: 'bg-red-500/20',
          icon: icon || '⚠️'
        };
      case 'opportunity':
        return {
          bg: 'bg-blue-500/10 border-blue-500/30',
          text: 'text-blue-700 dark:text-blue-400',
          iconBg: 'bg-blue-500/20',
          icon: icon || '🚀'
        };
      default:
        return {
          bg: 'bg-purple-500/10 border-purple-500/30',
          text: 'text-purple-700 dark:text-purple-400',
          iconBg: 'bg-purple-500/20',
          icon: icon || '💡'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`flex items-start space-x-6 p-8 rounded-2xl border-2 ${styles.bg} backdrop-blur-sm transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl`}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-16 h-16 rounded-xl ${styles.iconBg} flex items-center justify-center text-3xl shadow-lg`}>
        {styles.icon}
      </div>

      {/* Text */}
      <div className="flex-1">
        <p className={`text-xl ${styles.text} leading-relaxed font-medium`}>
          {text}
        </p>
      </div>
    </motion.div>
  );
};

const SlideInsights = ({ title, insights = [] }) => {
  // Group insights by type
  const strengths = insights.filter(i => i.type === 'strength');
  const risks = insights.filter(i => i.type === 'risk');
  const opportunities = insights.filter(i => i.type === 'opportunity');

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 p-16 overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-3 mb-2">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-2xl">
            🧠
          </div>
          <h2 className="text-4xl font-bold text-gray-800 dark:text-white">
            {title || 'Clara Insights'}
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 ml-16">
          KI-gestützte Analyse und Empfehlungen
        </p>
      </motion.div>

      {/* Insights Grid */}
      <div className="grid grid-cols-1 gap-8 flex-1">
        {/* Strengths Section */}
        {strengths.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold text-green-700 dark:text-green-400 mb-4 flex items-center space-x-2">
              <span>✅</span>
              <span>Stärken</span>
            </h3>
            <div className="space-y-3">
              {strengths.map((insight, index) => (
                <InsightItem
                  key={`strength-${index}`}
                  text={insight.text}
                  icon={insight.icon}
                  type="strength"
                  delay={0.3 + index * 0.1}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Risks Section */}
        {risks.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center space-x-2">
              <span>⚠️</span>
              <span>Risiken</span>
            </h3>
            <div className="space-y-3">
              {risks.map((insight, index) => (
                <InsightItem
                  key={`risk-${index}`}
                  text={insight.text}
                  icon={insight.icon}
                  type="risk"
                  delay={0.5 + index * 0.1}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Opportunities Section */}
        {opportunities.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className="text-xl font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center space-x-2">
              <span>🚀</span>
              <span>Chancen</span>
            </h3>
            <div className="space-y-3">
              {opportunities.map((insight, index) => (
                <InsightItem
                  key={`opportunity-${index}`}
                  text={insight.text}
                  icon={insight.icon}
                  type="opportunity"
                  delay={0.7 + index * 0.1}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* AI Attribution */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-8 flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-600"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
        </svg>
        <span>Analysiert von Clara AI • Powered by Gemini 2.0 Flash</span>
      </motion.div>
    </div>
  );
};

export default SlideInsights;

